import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { LlmIntegrationService } from 'src/app/core/services/llm-integration.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { FormalizeCompany, FormalizePayload, FormalizeUser } from 'src/app/core/models/formalizePayload';

@Component({
  selector: 'app-voice-input',
  templateUrl: './voice-input.component.html',
  styleUrls: ['./voice-input.component.css']
})
export class VoiceInputComponent implements OnInit {

  @Output() transcriptReady = new EventEmitter<string>();

  @Input() noteId: string = null;
  @Input() sourceScreen: string = '';
  @Input() candidate: any = null;
  @Input() requisition: any = null;
  @Input() jobMatch: any = null;
  @Input() company: any = null;

  isRecording = false;
  transcript = '';
  isSupported = false;
  error = '';
  showTranscriptConfirmation = false;
  
  private recognition: any;
  private finalTranscript = ''; // Store accumulated final input
  private requestStartTime: Date = null;

  constructor(private gpt: LlmIntegrationService, private authService: AuthService) {
    // Check for browser support
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this.isSupported = !!SpeechRecognition;
    
    if (this.isSupported) {
      this.recognition = new SpeechRecognition();
      this.initializeRecognition();
    }
  }

  ngOnInit() {
  }

  private initializeRecognition() {
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';

    this.recognition.onstart = () => {
      this.isRecording = true;
      this.error = '';
      this.transcript = '';
      this.finalTranscript = ''; // Reset on start
      this.showTranscriptConfirmation = false;
    };

    this.recognition.onresult = (event: any) => {
      let interim = '';

      // Process all results from the current event
      for (let i = event.resultIndex; i < event.results.length; i++) {
        let transcript = event.results[i][0].transcript;

        // Convert spoken punctuation to actual symbols
        transcript = this.convertSpokenPunctuation(transcript);

        if (event.results[i].isFinal) {
          // Check if transcript ends with punctuation (after conversion)
          const endsWithPunctuation = /[.,?!;:'"\-]$/.test(transcript.trim());
          
          if (endsWithPunctuation) {
            // Remove trailing space before adding punctuation
            this.finalTranscript = this.finalTranscript.replace(/\s+$/, '') + transcript;
          } else {
            // Add space after regular words
            this.finalTranscript += transcript + ' ';
          }
        } else {
          // Only use interim for display, don't store
          interim += transcript;
        }
      }

      // Display accumulated final + current interim
      this.transcript = (this.finalTranscript + interim).trim();
    };

    this.recognition.onerror = (event: any) => {
      this.error = `Error: ${event.error}`;
      console.error('Speech recognition error:', event.error);
    };

    this.recognition.onend = () => {
      this.isRecording = false;
      // Show confirmation toast instead of directly emitting
      if (this.transcript.trim()) {
        this.showTranscriptConfirmation = true;
      }
    };
  }

  toggleRecording() {
    if (!this.isSupported) {
      this.error = 'Speech recognition not supported in your browser. Try Chrome or Edge.';
      return;
    }

    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  startRecording() {
    if (this.recognition && !this.isRecording) {
      this.transcript = '';
      this.error = '';
      this.showTranscriptConfirmation = false;
      this.requestStartTime = new Date();
      this.recognition.start();
    }
  }

  stopRecording() {
    if (this.recognition && this.isRecording) {
      this.recognition.stop();
    }
  }

  // Use this transcript - emit to parent
  useTranscript() {
    if (this.transcript.trim()) {
      const text = this.transcript.trim();
      this.saveFormalizationActivity(text, text);
      this.transcriptReady.emit(text);
      this.clearAll();
    }
  }

  // Re-record - start fresh
  reRecord() {
    this.transcript = '';
    this.error = '';
    this.showTranscriptConfirmation = false;
    this.startRecording();
  }

  // Clear everything
  clearAll() {
    this.transcript = '';
    this.error = '';
    this.showTranscriptConfirmation = false;
    this.finalTranscript = '';
  }

  // Add punctuation to transcript
  addPunctuation(symbol: string) {
    if (this.transcript.trim() || this.finalTranscript.trim()) {
      this.transcript += symbol;
      this.finalTranscript += symbol;
    }
  }

  // Remove last character
  removeLastChar() {
    if (this.transcript.length > 0) {
      this.transcript = this.transcript.slice(0, -1);
    }
    if (this.finalTranscript.length > 0) {
      this.finalTranscript = this.finalTranscript.slice(0, -1);
    }
  }

  saveFormalizationActivity(originalText: string, formalizedText: string) {
    const endTime = new Date();
    const startTime = this.requestStartTime || endTime;
    const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000);
    const userDetails = this.authService.getUserDetails();

    const payload = new FormalizePayload({
      duration: duration,
      dateRange: {
        startDate: startTime.toISOString(),
        endDate: endTime.toISOString()
      },
      sourceScreen: this.sourceScreen || null,
      company: this.company ? new FormalizeCompany(this.company.companyId, this.company.companyName, '', '') : null,
      jobMatch: this.jobMatch || null,
      requisition: this.requisition || null,
      candidate: this.candidate || null,
      user: userDetails ? new FormalizeUser(
        userDetails.id,
        userDetails.firstname,
        userDetails.lastname,
        userDetails.emailId
      ) : null,
      originalText: "",
      formalizedText: formalizedText,
      mode: 'VOICE',
      tone: null,
      savedNote: null,
      noteId: this.noteId || null
    });

    this.gpt.saveTextFormalization(payload, (response) => {
      // Activity logged silently
    }, (error) => {
      console.error('Failed to save voice input activity', error);
    });
  }

  // Convert spoken punctuation words to actual symbols
  private convertSpokenPunctuation(text: string): string {
    const punctuationMap: { [key: string]: string } = {
      'period': '.',
      'dot': '.',
      'comma': ',',
      'question mark': '?',
      'question': '?',
      'exclamation': '!',
      'exclamation mark': '!',
      'semicolon': ';',
      'colon': ':',
      'apostrophe': "'",
      'hyphen': '-',
      'dash': '-',
      'new line': '\n',
      'newline': '\n'
    };

    let result = text;
    
    // Replace spoken punctuation words with actual symbols
    // Case-insensitive matching
    for (const [word, symbol] of Object.entries(punctuationMap)) {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      result = result.replace(regex, symbol);
    }

    // Clean up any spaces that end up directly before punctuation
    // e.g. "role ." -> "role." , "clearly ?" -> "clearly?"
    result = result.replace(/\s+([.,?!;:'"\-])/g, '$1');

    return result;
  }

}
